import { Stack, PrimaryButton } from "@fluentui/react";
import { ErrorCircle24Regular } from "@fluentui/react-icons";

import styles from "./Answer.module.css";

/**
 * The Props interface defines the properties required for handling error states and
 * providing a mechanism to retry certain actions within a component.
 *
 * Properties:
 * @property {string} error - A string representing the error message. This property indicates
 * the error currently being handled and is used to display feedback to the user.
 *
 * @property {() => void} onRetry - A callback function that is executed when a retry action
 * is triggered. This function provides the logic necessary to attempt the process again,
 * allowing for recovery from the error condition.
 */
interface Props {
    error: string;
    onRetry: () => void;
}

/**
 * A React functional component that displays an error message along with a retry button.
 * It is styled using CSS classes defined in `styles`.
 *
 * @param {Object} props - The component props.
 * @param {string} props.error - The error message to be displayed.
 * @param {Function} props.onRetry - The callback function to be executed when the retry button is clicked.
 * @returns {JSX.Element} A JSX element containing an error message and a retry button.
 */
export const AnswerError = ({ error, onRetry }: Props) => {
    return (
        <Stack className={styles.answerContainer} verticalAlign="space-between">
            <ErrorCircle24Regular aria-hidden="true" aria-label="Error icon" primaryFill="red" />

            <Stack.Item grow>
                <p className={styles.answerText}>{error}</p>
            </Stack.Item>

            <PrimaryButton className={styles.retryButton} onClick={onRetry} text="Retry" />
        </Stack>
    );
};
